import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { debounce, isEmpty, get } from 'lodash';
import { Sidebar } from '@src/components/Sidebar';
import { Button } from '@src/components/Button';
import { Container } from '@src/components/Container';
import { idify } from '@src/util/idify';
import { FUNNEL_MESSAGE_ROUTE, FUNNEL_PITCH_ROUTE } from '@src/Routes/links';
import { MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackViewCreateMessage, trackViewCreatePost } from '@src/metrics';
import { QUESTION_ROUTE } from '@src/components/Preview/appRoutes';
import apolloClient from '@src/graphql/apollo';
import { OpeningCandidateDestinationTypeEnum, SaveOpeningDocument } from '@src/graphql/generated';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { isValidUrl } from '@src/util/url';
import { isPitchEnabled } from '../util/creationSteps';
import { AutomatedResponse } from './AutomatedResponse';
import AutomatedQuestions from './AutomatedQuestions';
import CandidateDestination from './CandidateDestination';
import './styles.scss';

export default class CreateQuestions extends PureComponent {
  static propTypes = {
    company: PropTypes.object,
    opening: PropTypes.object.isRequired,
    recruiter: PropTypes.object,
  };

  state = {
    hasQuestions: undefined,
    opening: this.props.opening,
    question: undefined,
    saving: false,
  };

  saveOpening = debounce(async openingInput => {
    const {
      data: { saveOpening: updatedOpening },
    } = await apolloClient.mutate({
      mutation: SaveOpeningDocument,
      variables: {
        openingId: this.state.opening.id,
        openingInput: openingInput,
      },
    });

    if (get(openingInput, 'automatedResponse')) {
      this.setState({ saving: false });
    } else {
      this.setState(({ opening }) => ({ opening: { ...opening, ...updatedOpening }, saving: false }));
    }
  }, 400);

  onFieldChange = changes => {
    this.setState(
      ({ opening }) => ({ opening: { ...opening, ...changes }, saving: true }),
      () => {
        if (
          changes?.candidateDestination?.type === OpeningCandidateDestinationTypeEnum.EXTERNAL_URL &&
          (!changes?.candidateDestination?.url ||
            (changes?.candidateDestination?.url && !isValidUrl(changes?.candidateDestination?.url)))
        ) {
          return;
        }

        this.saveOpening(changes);
      },
    );
  };

  setPreviewQuestion = data => {
    if (!data) {
      this.setState({ hasQuestions: undefined, question: undefined });
    } else {
      this.setState({
        hasQuestions: true,
        question: {
          content: [
            { type: 'chat-bubble', value: data.question },
            { key: 'note', type: 'chat-textarea', value: 'Your answer...' },
            {
              key: 'answer',
              type: 'buttons',
              value: [
                { key: 'positive', type: 'positive', value: 'Send' },
                { key: 'negative', type: 'negative', value: 'Skip' },
              ],
            },
          ],
          type: 'interview',
        },
      });
    }
  };

  render() {
    const { company } = this.props;

    const {
      opening,
      opening: { candidateDestination },
      question,
      saving,
      hasQuestions,
    } = this.state;

    const openingId = idify(opening);

    return (
      <div className="page create-opening funnels">
        <div className="page-layout">
          <Sidebar
            companyProfile={company?.profile}
            defaultTab={ActiveTab.PREVIEW}
            opening={opening}
            previewUrl={QUESTION_ROUTE}
            question={question}
          />

          <Container type={ContainerType.BORDERED}>
            <div className="page-section">
              <CandidateDestination candidateDestination={candidateDestination} onFieldChange={this.onFieldChange} />
            </div>

            {candidateDestination.type === OpeningCandidateDestinationTypeEnum.MEETFRANK && (
              <Fragment>
                <div className="page-section">
                  <AutomatedQuestions
                    interviewQuestions={opening.interviewQuestions}
                    onFieldChange={this.onFieldChange}
                    openingId={openingId}
                    saving={saving}
                    setPreviewQuestion={this.setPreviewQuestion}
                  />
                </div>

                <div className="page-section">
                  <AutomatedResponse
                    automatedResponse={opening.automatedResponse}
                    company={company}
                    onFieldChange={this.onFieldChange}
                    opening={opening}
                    openingId={openingId}
                    openingTitle={opening.title}
                  />
                </div>
              </Fragment>
            )}

            <div className="page-section">
              <div className="create-questions-navigation-buttons">
                <Button
                  isDisabled={
                    (candidateDestination.type === OpeningCandidateDestinationTypeEnum.MEETFRANK &&
                      hasQuestions === undefined) ||
                    (candidateDestination.type === OpeningCandidateDestinationTypeEnum.MEETFRANK &&
                      isEmpty(opening.interviewQuestions) &&
                      !!opening.automatedResponse) ||
                    (candidateDestination.type === OpeningCandidateDestinationTypeEnum.MEETFRANK &&
                      !isEmpty(opening.interviewQuestions) &&
                      !opening.automatedResponse) ||
                    (candidateDestination.type === OpeningCandidateDestinationTypeEnum.EXTERNAL_URL &&
                      !isPitchEnabled(opening)) ||
                    saving
                  }
                  isLink
                  onClick={() =>
                    trackViewCreatePost({
                      'Event Source': MetricEventSource.CONTINUE_BUTTON,
                      'Opening Id': openingId,
                      'Previous Context': MetricContext.CREATE_OPENING_INTERVIEW,
                    })
                  }
                  params={{ openingId }}
                  to={FUNNEL_PITCH_ROUTE}
                >
                  Continue
                </Button>

                <Button
                  appearance="invisible"
                  className="create-questions__back-button"
                  isLink
                  onClick={() =>
                    trackViewCreateMessage({
                      'Event Source': MetricEventSource.BACK_BUTTON,
                      'Opening Id': openingId,
                      'Previous Context': MetricContext.CREATE_OPENING_INTERVIEW,
                    })
                  }
                  params={{ openingId }}
                  to={FUNNEL_MESSAGE_ROUTE}
                >
                  Back
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }
}
